import { Divider, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useStore } from "zustand";
import { createZoneStyleByType, type ZoneType } from "@designboticteam/kolega-3d";
import { Button, ToolControl } from "@designboticteam/react/components";
import { SelectTool } from "@designboticteam/workspace-3d";
import { Section } from "@/components/Section";
import { ZoneTypeSelector } from "@/components/ZoneTypeSelector";
import { useEngineApi } from "@/engine";

const ShapesModificationSection = (): React.ReactNode => {
	const api = useEngineApi();
	const zoneStyleByType = createZoneStyleByType(api.palette);
	const selectedShape = useStore(api.workspace3D.store, (state) => state.tools.selectedShape);
	const [selectedZoneType, setSelectedZoneType] = useState<ZoneType | null>(null);
	const [cloneOffset, setCloneOffset] = useState("0");

	const selectedEdgeIndices = useStore(api.workspace3D.store, (state) => state.tools.selectedEdgeIndices);

	useEffect(() => {
		setSelectedZoneType(selectedShape ? api.kolega3D.zones.getType(selectedShape) : null);
		setCloneOffset("0");
	}, [selectedShape, api]);

	return (
		<Section title="Selection" isCollapsible={true}>
			<Stack direction="column" sx={{ gap: 2 }}>
				<ToolControl store={api.workspace3D.store} setActiveTool={api.workspace3D.tools.setActiveTool} label="Select" tool={SelectTool} />

				{selectedShape && selectedZoneType && (
					<Stack
						direction="column"
						sx={{
							gap: 2,
							borderLeft: "3px solid",
							borderColor: zoneStyleByType[selectedZoneType].color,
							paddingLeft: 2,
						}}
					>
						<Typography
							variant="overline"
							sx={{
								color: zoneStyleByType[selectedZoneType].color,
							}}
						>
							Selected: {selectedZoneType}
						</Typography>

						<Stack direction="column">
							<ZoneTypeSelector
								value={selectedZoneType}
								onChange={(zoneType) => {
									api.kolega3D.zones.changeSelectedType(zoneType);
									setSelectedZoneType(zoneType);
								}}
							/>
						</Stack>

						<Divider />

						<EdgeOffsetControls key={selectedEdgeIndices.join(",")} selectedEdgeCount={selectedEdgeIndices.length} />

						<Divider />
						<Stack direction="column" sx={{ gap: 1 }}>
							<Typography variant="caption">Clone</Typography>

							<Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
								<TextField
									size="small"
									type="text"
									label="Offset (m)"
									value={cloneOffset}
									onChange={(event) => {
										const next = event.target.value;

										// Accept only numeric input, including intermediate states (empty, "-", "-.5")
										// so a negative offset (outward) can actually be typed.
										if (/^-?\d*\.?\d*$/.test(next)) {
											setCloneOffset(next);
										}
									}}
									slotProps={{ htmlInput: { inputMode: "decimal" } }}
								/>

								<Button
									isFullWidth={true}
									onClick={() => {
										const parsed = parseFloat(cloneOffset);
										api.kolega3D.zones.cloneSelectedWithOffset(Number.isFinite(parsed) ? parsed : 0);
										setCloneOffset("0");
									}}
								>
									Clone
								</Button>
							</Stack>
						</Stack>

						<Divider />

						<Button isFullWidth={true} onClick={api.kolega3D.tools.deleteSelectedShape}>
							Delete
						</Button>
					</Stack>
				)}
			</Stack>
		</Section>
	);
};

const EdgeOffsetControls = ({ selectedEdgeCount }: { selectedEdgeCount: number }): React.ReactNode => {
	const api = useEngineApi();
	const [edgeOffset, setEdgeOffset] = useState("0");

	const parsedEdgeOffset = Number.parseFloat(edgeOffset);
	const canApplyEdgeOffset = selectedEdgeCount > 0 && Number.isFinite(parsedEdgeOffset);

	return (
		<Stack direction="column" sx={{ gap: 1 }}>
			<Typography variant="caption">Resize</Typography>

			<Typography variant="body2">{selectedEdgeCount === 0 ? "Click an edge to select it" : `Selected edges: ${selectedEdgeCount}`}</Typography>

			<div style={{ marginBottom: "16px" }}>
				<Button isFullWidth={true} onClick={() => api.workspace3D.tools.selectAllEdges()}>
					Select all edges
				</Button>
			</div>

			<Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
				<TextField
					size="small"
					type="text"
					label="Offset (m)"
					value={edgeOffset}
					disabled={selectedEdgeCount === 0}
					onChange={(event) => {
						const next = event.target.value;

						if (/^-?\d*\.?\d*$/.test(next)) {
							setEdgeOffset(next);
						}
					}}
					slotProps={{ htmlInput: { inputMode: "decimal" } }}
				/>

				<Button
					isFullWidth={true}
					onClick={() => {
						if (canApplyEdgeOffset) {
							api.workspace3D.tools.setEdgeOffset(parsedEdgeOffset);
						}
					}}
				>
					Apply
				</Button>
			</Stack>
		</Stack>
	);
};

export { ShapesModificationSection };
